import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import SlidePanel from '../shared/SlidePanel';
import agentService from '../../_services/agent.service';
import { hapticTick } from '../../_utils/haptics';
import { Icon } from './BaileyBits';
import { threadTitle, when, dayGroup } from './bailey/format';
import { howIWork } from './bailey/ideas';

const PAGE = 30;
const GROUP_ORDER = ['Today', 'Yesterday', 'This week', 'This month', 'Earlier'];
const stamp = (r) => r.lastMessageAt || r.createdAt;
// What Bailey always does on the front desk, whatever its soul.
const WITH_CLIENTS = [
    'I always say I’m an AI, first thing.',
    'I only quote your real prices and free times.',
    'I never take card details.',
    'Health questions go to your team.',
    'If I can’t help, I take a message or put the call through.',
    'You can read every conversation.'
];

/**
 * Every conversation, newest first. Older pages load with `before`; a server
 * that doesn't page yet sends the newest rows back, which is taken as the end.
 */
export function useConversations() {
    const [rows, setRows] = useState([]);
    const [loaded, setLoaded] = useState(false);
    const [error, setError] = useState('');
    const [more, setMore] = useState(false);
    const [busyMore, setBusyMore] = useState(false);
    const rowsRef = useRef(rows);
    rowsRef.current = rows;
    // Once an older page comes back empty, there is nothing more to offer.
    const endRef = useRef(false);

    const refresh = useCallback(() => agentService.sessions({ limit: PAGE })
        .then((res) => {
            const list = (res.data && res.data.responseData) || [];
            // Keep older pages already loaded, under the fresh first page.
            setRows((cur) => {
                const seen = new Set(list.map((r) => String(r._id)));
                return list.concat(cur.slice(PAGE).filter((r) => !seen.has(String(r._id))));
            });
            setMore(!endRef.current && list.length >= PAGE);
            setError('');
        })
        .catch(() => setError('I couldn’t load your conversations just now.'))
        .finally(() => setLoaded(true)), []);

    const loadOlder = useCallback(async () => {
        const cur = rowsRef.current;
        if (busyMore || !cur.length) return;
        setBusyMore(true);
        try {
            const res = await agentService.sessions({ before: stamp(cur[cur.length - 1]), limit: PAGE });
            const list = (res.data && res.data.responseData) || [];
            const seen = new Set(cur.map((r) => String(r._id)));
            const fresh = list.filter((r) => !seen.has(String(r._id)));
            setRows((c) => c.concat(fresh));
            if (fresh.length < PAGE) endRef.current = true;
            setMore(fresh.length >= PAGE);
        } catch (e) {
            setError('I couldn’t load older conversations just now.');
        }
        setBusyMore(false);
    }, [busyMore]);

    // Drop one row now, before the server answers (a deleted thread).
    const remove = useCallback((id) => setRows((cur) => cur.filter((r) => String(r._id) !== String(id))), []);

    return { rows, loaded, error, more, busyMore, refresh, loadOlder, remove };
}

/** One conversation in a list: open it, pin it, or delete it. */
function ThreadRow({ row, active, pinned, canPin, locked, onOpen, onTogglePin, onDelete }) {
    const title = threadTitle(row);
    return (
        <div className={`bly-tl-row${active ? ' is-active' : ''}${pinned ? ' is-pinned' : ''}`}>
            <button
                type="button"
                className="bly-tl-open"
                onClick={() => { hapticTick(); onOpen(row._id); }}
                aria-current={active ? 'true' : undefined}
                disabled={locked && !active}
                // The list cuts titles short; the whole one on hover.
                title={locked && !active ? 'Opens once Bailey has replied' : title}
            >
                <span className="bly-tl-title">{title}</span>
                <small>{when(stamp(row))}</small>
            </button>
            {canPin && (
                <button
                    type="button"
                    className="bly-tl-pin"
                    aria-pressed={pinned}
                    aria-label={pinned ? `Unpin “${title}”` : `Pin “${title}”`}
                    title={pinned ? 'Unpin' : 'Pin to the top'}
                    onClick={() => { hapticTick(); onTogglePin(row); }}
                >
                    <Icon.Pin size={15} filled={pinned} />
                </button>
            )}
            {onDelete && (
                <button
                    type="button"
                    className="bly-tl-del"
                    aria-label={`Delete “${title}”`}
                    title="Delete conversation"
                    disabled={locked && active}
                    onClick={() => { hapticTick(); onDelete(row); }}
                >
                    <Icon.Close size={14} />
                </button>
            )}
        </div>
    );
}

/**
 * The list itself: Pinned first, then by day, filtered by the search box.
 * Shared by the desktop sidebar and the phone panel.
 */
export function ThreadList({ convos, pinned, isPinned, canPin, locked, activeId, onOpen, onTogglePin, onDelete, undo, q }) {
    const groups = useMemo(() => {
        const needle = (q || '').trim().toLowerCase();
        const match = (r) => !needle || threadTitle(r).toLowerCase().includes(needle);
        const out = [];
        const pins = pinned.filter(match);
        if (pins.length) out.push({ name: 'Pinned', rows: pins });
        const by = {};
        convos.rows.filter((r) => !isPinned(r._id) && match(r)).forEach((r) => {
            const g = dayGroup(stamp(r));
            (by[g] = by[g] || []).push(r);
        });
        GROUP_ORDER.forEach((g) => { if (by[g]) out.push({ name: g, rows: by[g] }); });
        return out;
    }, [convos.rows, pinned, isPinned, q]);

    if (!convos.loaded) return <div className="bly-tl-note">Loading…</div>;
    return (
        <>
            {convos.error && <div className="bly-tl-note is-bad" role="alert">{convos.error}</div>}
            {undo && (
                <div className="bly-tl-undo" role="status">
                    <span>Conversation deleted</span>
                    <button type="button" onClick={undo.run}>Undo</button>
                </div>
            )}
            {!groups.length && !convos.error && (
                <div className="bly-tl-note">{q ? 'Nothing matches that.' : 'No conversations yet. Anything you ask Bailey is kept here.'}</div>
            )}
            {groups.map((g) => (
                <section key={g.name} className="bly-tl-group">
                    <h4>{g.name === 'Pinned' && <Icon.Pin size={12} filled />}{g.name}</h4>
                    {g.rows.map((r) => (
                        <ThreadRow
                            key={r._id}
                            row={r}
                            active={String(r._id) === String(activeId)}
                            pinned={isPinned(r._id)}
                            canPin={canPin}
                            locked={locked}
                            onOpen={onOpen}
                            onTogglePin={onTogglePin}
                            onDelete={onDelete}
                        />
                    ))}
                </section>
            ))}
            {convos.more && !q && (
                <button type="button" className="bly-tl-more" onClick={convos.loadOlder} disabled={convos.busyMore}>
                    {convos.busyMore ? 'Loading…' : 'Show older conversations'}
                </button>
            )}
        </>
    );
}

function Search({ value, onChange, inputRef }) {
    return (
        <label className="bly-search">
            <Icon.Search />
            <input ref={inputRef} type="search" value={value} onChange={(e) => onChange(e.target.value)} placeholder="Find a conversation" aria-label="Find a conversation" />
        </label>
    );
}

/**
 * Conversations: one list, always from the left, the same at every size.
 *
 * When the page has room it DOCKS beside the thread and pushes it over (the
 * way ChatGPT and Claude keep theirs). When it doesn't (tablet portrait,
 * phones) the same drawer slides OVER the page with the page dimmed behind;
 * tapping outside, Escape, a swipe to the left or opening a conversation
 * closes it.
 */
function DrawerBody({ onNew, onHome, atHome, onHide, hideLabel, searchRef, ...list }) {
    const [q, setQ] = useState('');
    return (
        <aside className="bly-side" aria-label="Bailey">
            {/* The panel and compose buttons sit exactly where the page header
                has them while the list is closed, so opening, peeking or closing
                never moves them from under the pointer. */}
            <div className="bly-side-top">
                <button type="button" className="bly-iconbtn" onClick={() => { hapticTick(); onHide(); }} aria-label={hideLabel} title={hideLabel}>
                    <Icon.Sidebar size={20} />
                </button>
                <button type="button" className="bly-iconbtn" onClick={() => { hapticTick(); onNew(); }} aria-label="New conversation" title="New conversation">
                    <Icon.Compose size={19} />
                </button>
            </div>
            <div className="bly-side-actions">
                {/* The way back to everything Bailey can do: what needs you,
                    what it noticed, what's on the go, and ideas. */}
                <button
                    type="button"
                    className={`bly-side-home${atHome ? ' is-active' : ''}`}
                    onClick={() => { hapticTick(); onHome(); }}
                    aria-current={atHome ? 'page' : undefined}
                >
                    <Icon.Home size={18} />
                    <span>Home</span>
                </button>
                <Search value={q} onChange={setQ} inputRef={searchRef} />
            </div>
            <h3 className="bly-side-label">Conversations</h3>
            <div className="bly-side-scroll">
                <ThreadList {...list} q={q} />
            </div>
        </aside>
    );
}

/** Docked: always mounted, so opening and closing can push the page smoothly. */
export function ConversationDock({ open, ...rest }) {
    return (
        <div className={`bly-dock-side${open ? '' : ' is-closed'}`} aria-hidden={open ? undefined : 'true'} inert={open ? undefined : ''}>
            <DrawerBody {...rest} hideLabel="Hide conversations" />
        </div>
    );
}

/**
 * A peek at the list while it's hidden, the way Claude's opens: hovering the
 * panel icon floats it over the page without pushing anything; moving away
 * lets it go. "Keep open" docks it.
 */
export function ConversationPeek({ onDock, onClose, onEnter, onLeave, onOpen, onNew, onHome, ...list }) {
    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [onClose]);
    return (
        <div className="bly-peek" onMouseEnter={onEnter} onMouseLeave={onLeave}>
            <DrawerBody
                {...list}
                hideLabel="Keep conversations open"
                onHide={onDock}
                onNew={() => { onClose(); onNew(); }}
                onHome={() => { onClose(); onHome(); }}
                onOpen={(id) => { onClose(); onOpen(id); }}
            />
        </div>
    );
}

/** Over the page, for screens without room to dock. */
export function ConversationDrawer({ onClose, onOpen, onNew, onHome, returnFocus, ...list }) {
    const [closing, setClosing] = useState(false);
    const panel = useRef(null);
    const start = useRef(null);
    const close = useCallback((then) => {
        if (closing) return;
        setClosing(true);
        setTimeout(() => {
            onClose();
            if (typeof then === 'function') then();
            if (returnFocus && returnFocus.current) returnFocus.current.focus();
        }, 220);
    }, [closing, onClose, returnFocus]);

    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') close(); };
        document.addEventListener('keydown', onKey);
        // Focus moves into the drawer (the drawer itself, so no button lights
        // up with a focus ring on a tap); Tab carries on from there.
        if (panel.current) panel.current.focus({ preventScroll: true });
        return () => document.removeEventListener('keydown', onKey);
    }, [close]);

    // A swipe to the left closes it, as it came in from the left.
    const onTouchStart = (e) => { const t = e.touches[0]; start.current = { x: t.clientX, y: t.clientY }; };
    const onTouchMove = (e) => {
        if (!start.current) return;
        const t = e.touches[0];
        const dx = t.clientX - start.current.x;
        if (dx < -60 && Math.abs(dx) > Math.abs(t.clientY - start.current.y)) { start.current = null; close(); }
    };

    return (
        <div className={`bly-drawer${closing ? ' is-closing' : ''}`}>
            <div className="bly-drawer-scrim" onClick={() => close()} aria-hidden="true" />
            <div
                className="bly-drawer-panel"
                ref={panel}
                role="dialog"
                aria-modal="true"
                aria-label="Bailey"
                tabIndex={-1}
                onTouchStart={onTouchStart}
                onTouchMove={onTouchMove}
            >
                <DrawerBody
                    {...list}
                    hideLabel="Close conversations"
                    onHide={() => close()}
                    onNew={() => close(onNew)}
                    onHome={() => close(onHome)}
                    onOpen={(id) => close(() => onOpen(id))}
                />
            </div>
        </div>
    );
}

/**
 * What Bailey can see, what it can do, and what always waits for the owner.
 * Built from status.scopes and status.tools, so it never claims more than
 * this account allows.
 */
export function HowIWorkPanel({ status, frontDesk = false, onClose }) {
    const h = howIWork(status);
    const staff = status && status.principalType === 'employed-staff';
    return (
        <SlidePanel title="How I work" onClose={onClose} width={440} className="bly-panel">
            <div className="bly-panel-body bly-how">
                <p className="bly-how-lede">
                    I’m here to take the busywork off your plate. I work from what’s already in your Beu account, and anything that reaches a client waits for your OK.
                </p>
                {!!h.asks.length && (
                    <section className="bly-how-sec is-key">
                        <h4><Icon.Shield size={16} />Always waits for you</h4>
                        <ul>{h.asks.map((t) => <li key={t}>{t}</li>)}</ul>
                    </section>
                )}
                {!!h.can.length && (
                    <section className="bly-how-sec">
                        <h4>What I can do</h4>
                        <ul>{h.can.map((t) => <li key={t}><span className="bly-how-tick"><Icon.Tick size={12} /></span>{t}</li>)}</ul>
                    </section>
                )}
                {frontDesk && (
                    <section className="bly-how-sec">
                        <h4><Icon.Headset size={16} />With your clients</h4>
                        <ul>{WITH_CLIENTS.map((t) => <li key={t}><span className="bly-how-tick"><Icon.Tick size={12} /></span>{t}</li>)}</ul>
                    </section>
                )}
                {!!h.see.length && (
                    <section className="bly-how-sec">
                        <h4>What I can see</h4>
                        <ul>{h.see.map((t) => <li key={t}><span className="bly-how-tick"><Icon.Tick size={12} /></span>{t}</li>)}</ul>
                        <p className="bly-how-note">{staff ? 'Only your own bookings, clients and takings.' : 'Only your own salon. Nothing outside Beu.'}</p>
                    </section>
                )}
                {!h.canAct && (
                    <section className="bly-how-sec">
                        <h4>Read only for now</h4>
                        <p className="bly-how-note">I can look things up and answer questions, but I can’t write or send anything on this account yet.</p>
                    </section>
                )}
            </div>
        </SlidePanel>
    );
}
